// Vuex
import { GetterTree, ActionContext } from 'vuex'

// Types
import { State, TypedActionTree, LoadingErrorState } from '@/store/types'

// Service
import PagemetaService from '@/services/PagemetaService'

// Define actions for Pagemeta
export type PagemetaActions = 'fetchProductURL'

// Define the type for the action context
type PagemetaActionContext = ActionContext<PagemetaState, State>

// Define state interface
interface ProductURLMap {
  [liheId: number]: string
}

export interface PagemetaState extends LoadingErrorState<PagemetaActions> {
  urlMap: ProductURLMap
}

// Initial state
const state: PagemetaState = {
  urlMap: {},
  loading: {},
  error: {},
}

// Mutation payloads
interface SetURLPayload {
  liheId: number
  productUrl: string
}

// Mutations
const mutations = {
  setUrl(state: PagemetaState, payload: SetURLPayload) {
    state.urlMap[payload.liheId] = payload.productUrl
  },
  setLoading(
    state: PagemetaState,
    { action, status }: { action: PagemetaActions; status: boolean }
  ) {
    state.loading[action] = status
  },
  setError(
    state: PagemetaState,
    { action, message }: { action: PagemetaActions; message: string | null }
  ) {
    state.error[action] = message
  },
}

// Actions using TypedActionTree
const actions: TypedActionTree<PagemetaActions, PagemetaState, State> = {
  async fetchProductURL(
    { commit, getters }: PagemetaActionContext,
    payload: { liheId: number }
  ) {
    const action = 'fetchProductURL'
    commit('setLoading', { action, status: true })
    commit('setError', { action, message: null })
    try {
      const { liheId } = payload

      // Try to get the URL from the state, if it exists
      const storedUrl: string | undefined = getters.getProductUrl(liheId)
      if (storedUrl) return

      const pagemetaService = new PagemetaService()
      const productUrl: string = await pagemetaService.getProductURL(liheId)
      commit('setUrl', { liheId, productUrl })
    } catch (error) {
      console.error('Error fetching product URL:', error)
      commit('setError', {
        action,
        message: 'Failed to fetch product URL.',
      })
    } finally {
      commit('setLoading', { action, status: false })
    }
  },
}

// Getters
const getters: GetterTree<PagemetaState, State> = {
  /**
   * Check if any action is loading
   */
  isLoadingAny: (state: PagemetaState) => {
    return Object.values(state.loading).some(status => status)
  },

  /**
   * Check if a specific action is loading
   */
  isLoading: (state: PagemetaState) => (action: PagemetaActions) => {
    return !!state.loading[action]
  },

  /**
   * Check if there are any errors
   */
  hasErrorAny: (state: PagemetaState) => {
    return Object.values(state.error).some(message => message !== null)
  },

  /**
   * Get the error message for a specific action
   */
  getError: (state: PagemetaState) => (action: PagemetaActions) => {
    return state.error[action] || null
  },

  /**
   * Get a product URL by liheId
   */
  getProductUrl:
    (state: PagemetaState) =>
    (liheId: number): string | undefined => {
      return state.urlMap[liheId]
    },
}

// Export the Vuex module
export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
